'use client';

import AutoScroll from 'embla-carousel-auto-scroll';
import useEmblaCarousel from 'embla-carousel-react';
import { motion } from 'motion/react';
import React, { useCallback, useState } from 'react';

import { ImageMedia } from '@/components/ImageMedia';
import type { MediaInfiniteCarousel as MediaInfiniteCarouselProps } from '@/payload-types';
import { cn } from '@/utils/cn';

import { BlockSettings } from '../BlockSettings';
import styles from './block.module.scss';
import { CarouselPlayPauseButton } from './CarouselPlayPauseButton';

const itemVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 50 },
};

export const MediaInfiniteCarousel: React.FC<MediaInfiniteCarouselProps> = (
  props
) => {
  const {
    backgroundColor,
    individualSpacing,
    medias,
    spacing,
    spacingBottom,
    spacingTop,
  } = props;
  const [playing, setPlaying] = useState(true);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'start',
    },
    [
      AutoScroll({
        stopOnInteraction: false,
        speed: 0.5,
      }),
    ]
  );

  const toggleAutoplay = useCallback(() => {
    const autoScroll = emblaApi?.plugins()?.autoScroll;
    if (!autoScroll) return;

    const isCurrentlyPlaying = autoScroll.isPlaying();

    if (isCurrentlyPlaying) {
      autoScroll.stop();
    } else {
      autoScroll.play();
    }

    setPlaying((prev) => !prev);
  }, [emblaApi]);

  if (!medias || !Array.isArray(medias) || medias.length === 0) {
    return null;
  }

  return (
    <BlockSettings
      backgroundColor={backgroundColor}
      individualSpacing={individualSpacing}
      spacing={spacing}
      spacingBottom={spacingBottom}
      spacingTop={spacingTop}
    >
      <div className={styles.wrapper}>
        <div ref={emblaRef}>
          <motion.div
            initial='hidden'
            whileInView='visible'
            viewport={{ once: true }}
            variants={{
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.3,
                },
              },
            }}
            className={cn(styles.list)}
          >
            {medias.map((item, index) => {
              const { aspectRatio, image } = item;

              if (!image) {
                return null;
              }

              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  style={{ willChange: 'opacity, transform' }}
                  transition={{ duration: 0.3 }}
                  className={cn(styles.item, styles[aspectRatio || 'portrait'])}
                >
                  <ImageMedia
                    aspectRatio={
                      aspectRatio === 'square' ? 'trueSquare' : aspectRatio
                    }
                    image={image}
                    responsiveWidths={
                      aspectRatio === 'square'
                        ? {
                            mobile: 343,
                            desktop: 512,
                            largeDesktop: 512,
                          }
                        : aspectRatio === 'portrait'
                          ? {
                              mobile: 343,
                              desktop: 512,
                              largeDesktop: 800,
                            }
                          : {
                              mobile: 343,
                              desktop: 1352,
                              largeDesktop: 2256,
                            }
                    }
                  />
                </motion.div>
              );
            })}
          </motion.div>
        </div>
        <CarouselPlayPauseButton
          clickHandler={toggleAutoplay}
          playing={playing}
        />
      </div>
    </BlockSettings>
  );
};
